import {
  createElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { ButtonHTMLAttributes, ComponentPropsWithRef, HTMLAttributes, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/shared/lib/cx';
import { getActiveModal, subscribeToModalLayer } from '../modalLayer';
import styles from './Primitives.module.css';

type ButtonVariant = 'primary' | 'secondary' | 'surface' | 'ghost' | 'danger';
export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
}
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth,
  loading,
  disabled,
  leading,
  trailing,
  className,
  children,
  type = 'button',
  ref,
  ...props
}: ButtonProps) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        className
      )}
      {...props}
    >
      {loading ? <Spinner size="sm" /> : leading}
      {children}
      {trailing}
    </button>
  );
}
export function IconButton({
  variant = 'ghost',
  className,
  ref,
  ...props
}: Omit<ButtonProps, 'leading' | 'trailing' | 'fullWidth'>) {
  return (
    <Button ref={ref} variant={variant} className={cx(styles.iconButton, className)} {...props} />
  );
}

interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: 'span' | 'p' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'button';
  variant?: 'body' | 'caption' | 'subheadline' | 'title';
  weight?: 'regular' | 'medium' | 'bold';
  type?: 'button';
}
export function Text({
  as = 'span',
  variant = 'body',
  weight = 'regular',
  className,
  ...props
}: TextProps) {
  return createElement(as, {
    ...props,
    className: cx(styles.text, styles[variant], styles[weight], className),
  });
}
export function List({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx(styles.list, className)} {...props} />;
}
interface SectionProps extends HTMLAttributes<HTMLElement> {
  header?: ReactNode;
  footer?: ReactNode;
}
export function Section({ header, footer, className, children, ...props }: SectionProps) {
  return (
    <section className={cx(styles.section, className)} {...props}>
      {header && <h2 className={styles.sectionHeader}>{header}</h2>}
      <div className={styles.sectionBody}>{children}</div>
      {footer && <div className={styles.sectionFooter}>{footer}</div>}
    </section>
  );
}
interface CellProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  leading?: ReactNode;
  trailing?: ReactNode;
  subtitle?: ReactNode;
  description?: ReactNode;
}
export function Cell({
  leading,
  trailing,
  subtitle,
  description,
  className,
  children,
  type = 'button',
  ...props
}: CellProps) {
  const content = (
    <>
      {leading && <span className={styles.cellLeading}>{leading}</span>}
      <span className={styles.cellBody}>
        <span className={styles.cellTitle}>{children}</span>
        {subtitle && <span className={styles.cellSubtitle}>{subtitle}</span>}
        {description && <span className={styles.cellSubtitle}>{description}</span>}
      </span>
      {trailing && <span className={styles.cellTrailing}>{trailing}</span>}
    </>
  );
  const classes = cx(styles.cell, props.onClick && styles.cellInteractive, className);
  return props.onClick ? (
    <button type={type} className={classes} {...props}>
      {content}
    </button>
  ) : (
    createElement('div', { ...props, className: classes }, content)
  );
}
interface InputProps extends ComponentPropsWithRef<'input'> {
  label?: ReactNode;
  invalid?: boolean;
}
export function Input({ label, invalid, className, id, ref, ...props }: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <label
      className={cx(styles.field, invalid && styles.fieldInvalid, className)}
      htmlFor={inputId}
    >
      {label && <span className={styles.fieldLabel}>{label}</span>}
      <input
        ref={ref}
        id={inputId}
        className={styles.input}
        aria-invalid={invalid || undefined}
        aria-label={label ? undefined : props.placeholder}
        {...props}
      />
    </label>
  );
}
export function Switch({ className, ref, ...props }: ComponentPropsWithRef<'input'>) {
  return (
    <input
      ref={ref}
      type="checkbox"
      role="switch"
      className={cx(styles.switch, className)}
      {...props}
    />
  );
}
export function Checkbox({ className, ref, ...props }: ComponentPropsWithRef<'input'>) {
  return (
    <label className={styles.controlTarget}>
      <input ref={ref} type="checkbox" className={cx(styles.checkbox, className)} {...props} />
    </label>
  );
}
export function Divider(props: HTMLAttributes<HTMLHRElement>) {
  return <hr className={styles.divider} {...props} />;
}
export function Avatar({
  initials,
  size = 48,
  className,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { initials: string; size?: number }) {
  return (
    <span
      aria-hidden="true"
      className={cx(styles.avatar, className)}
      style={{ width: size, height: size }}
      {...props}
    >
      {initials}
    </span>
  );
}
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span
      className={cx(styles.spinner, styles[`spinner_${size}`])}
      role="status"
      aria-label="Loading"
    />
  );
}
interface PlaceholderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}
export function Placeholder({
  title,
  description,
  action,
  children,
  className,
  ...props
}: PlaceholderProps) {
  return (
    <div className={cx(styles.placeholder, className)} {...props}>
      {children}
      {title && <h2 className={styles.placeholderTitle}>{title}</h2>}
      {description && <p className={styles.placeholderDescription}>{description}</p>}
      {action}
    </div>
  );
}
interface BannerProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
}
export function Banner({ icon, title, description, children, className, ...props }: BannerProps) {
  return (
    <aside className={cx(styles.banner, className)} {...props}>
      <div className={styles.bannerHead}>
        {icon}
        <div>
          {title && <h2 className={styles.bannerTitle}>{title}</h2>}
          {description && <p className={styles.bannerDescription}>{description}</p>}
        </div>
      </div>
      {children}
    </aside>
  );
}
export function Progress({ value, label = 'Remaining' }: { value: number; label?: string }) {
  return (
    <progress
      className={styles.progress}
      max={100}
      value={Math.min(100, Math.max(0, value))}
      aria-label={label}
    />
  );
}
interface ToastProps extends HTMLAttributes<HTMLDivElement> {
  icon?: ReactNode;
  action?: ReactNode;
  duration?: number;
  onDismiss: () => void;
}
export function Toast({
  icon,
  action,
  duration = 4000,
  onDismiss,
  className,
  children,
  ...props
}: ToastProps) {
  const dismissRef = useRef(onDismiss);
  const toastRef = useRef<HTMLDivElement>(null);
  const activeModal = useSyncExternalStore(subscribeToModalLayer, getActiveModal, () => null);
  const portalTarget = activeModal ?? document.body;
  useLayoutEffect(() => {
    const toast = toastRef.current;
    if (!toast || typeof toast.showPopover !== 'function') return;
    toast.showPopover();
    return () => {
      if (toast.matches(':popover-open')) toast.hidePopover();
    };
  }, [portalTarget]);
  useEffect(() => {
    dismissRef.current = onDismiss;
  }, [onDismiss]);
  useEffect(() => {
    const timer = window.setTimeout(() => dismissRef.current(), duration);
    return () => window.clearTimeout(timer);
  }, [duration]);
  return createPortal(
    <div
      ref={toastRef}
      popover="manual"
      role="status"
      className={cx(styles.toast, className)}
      {...props}
    >
      {icon}
      <span className={styles.toastBody}>{children}</span>
      {action}
      <IconButton aria-label="Dismiss notification" onClick={onDismiss}>
        ×
      </IconButton>
    </div>,
    portalTarget
  );
}
